import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import isEmpty from 'lodash/isEmpty';

import styles from './MobileCard.module.scss';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { MobileCardData, MobileFieldTypes, MobileField } from '../types';

import { Agreement, DateField, Field, Indicator, Line, Link, MobileCardButton, StatusIcon, Time } from './components';

interface Props<D extends MobileCardData = any> {
    index?: number;
    dataItem: D;
    fields: MobileField<D>[];
    onCardClick(params: D, e: React.MouseEvent, index?: number): void;
    onActionButtonClick?(index: number): void;
}

export enum IndicatorStyles {
    // @ts-expect-error
    Task = styles.task,
}

export enum Indicators {
    Task = 'task',
}

export const MobileCard = observer(function MobileCard<D extends MobileCardData = any>({
    index,
    dataItem,
    fields,
    onCardClick,
    onActionButtonClick,
}: Props<D>): React.ReactElement {
    const onButtonClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            if (!!onActionButtonClick && typeof index === 'number') {
                onActionButtonClick(index);
            }
        },
        [onActionButtonClick, index],
    );

    const onClick = useCallback(
        (event: React.MouseEvent) => {
            onCardClick?.(dataItem, event, index);
        },
        [onCardClick, dataItem, index],
    );

    const onLinkClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
    }, []);

    return (
        <li
            className={styles.root}
            onClick={onClick}
        >
            <div className={styles.fieldsContainer}>
                {fields.map(
                    (field, fieldIndex) =>
                        !field.hide && (
                            <React.Fragment key={`${fieldIndex}-${field.accessor}`}>
                                {getFieldContent(field, dataItem, onLinkClick, onButtonClick)}
                            </React.Fragment>
                        ),
                )}
            </div>
        </li>
    );
}) as <D extends MobileCardData = any>(props: Props<D>) => React.ReactElement;

function getFieldContent<D extends MobileCardData>(
    field: MobileField<D>,
    dataItem: D,
    onLinkClick: (e: React.MouseEvent) => void,
    onButtonClick: (e: React.MouseEvent) => void,
): Nullable<React.ReactElement<any>> {
    let content: Nullable<React.ReactElement<any>>;

    switch (field.fieldType) {
        case MobileFieldTypes.StatusIcon:
            content = dataItem.status !== undefined ? StatusIcon(dataItem.status, dataItem.isSignedWithEDS) : null;
            break;
        case MobileFieldTypes.Indicator:
            content = Indicator(dataItem[field.accessor] as Indicators);
            break;
        case MobileFieldTypes.Agreement:
            content = Agreement(dataItem[field.accessor] as boolean);
            break;
        case MobileFieldTypes.Text:
        case MobileFieldTypes.Phone:
            content = Field(field, dataItem);
            break;
        case MobileFieldTypes.Link:
            content = Link(field, dataItem, onLinkClick);
            break;
        case MobileFieldTypes.Button:
            content = MobileCardButton(field, onButtonClick);
            break;
        case MobileFieldTypes.Line:
            content = Line();
            break;
        case MobileFieldTypes.DateField:
            content = DateField(field, dataItem);
            break;
        case MobileFieldTypes.Time:
            content = Time(field, dataItem);
            break;
        default:
            content = null;
    }

    if (field.hideIfEmpty && isEmpty(dataItem[field.accessor])) {
        content = null;
    }

    return content;
}
